import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Calculator, TrendingUp, DollarSign, Percent, Package, ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import CurrencySelector from '../components/CurrencySelector';
import { useCurrency } from '../context/CurrencyContext';

const ROICalculatorPage = () => {
  const { format, formatCompact, currentCurrency } = useCurrency();
  const [products, setProducts] = useState(500);
  const [margin, setMargin] = useState(25);
  const [revenue, setRevenue] = useState(100000);

  const calculations = useMemo(() => {
    // Ensure minimum values
    const validProducts = Math.max(1, products);
    const validMargin = Math.max(0, margin);
    const validRevenue = Math.max(0, revenue);

    // Calculate complexity factor based on product count
    // More products = more pricing decisions = higher ROI from automation
    let complexityFactor = 1.0;
    if (validProducts >= 5000) {
      complexityFactor = 1.3;
    } else if (validProducts >= 2000) {
      complexityFactor = 1.2;
    } else if (validProducts >= 1000) {
      complexityFactor = 1.15;
    } else if (validProducts >= 500) {
      complexityFactor = 1.1;
    }
    
    // Conservative margin improvement range: 3-5% baseline
    // This is based on typical pricing optimization results
    const baselineMinImprovement = 3.0;
    const baselineMaxImprovement = 5.0;
    
    const minImprovementPoints = baselineMinImprovement * complexityFactor;
    const maxImprovementPoints = baselineMaxImprovement * complexityFactor;
    
    // Current monthly profit
    const currentProfit = validRevenue * (validMargin / 100);
    
    // Calculate new margins (additive percentage points)
    const improvedMarginMin = validMargin + minImprovementPoints;
    const improvedMarginMax = validMargin + maxImprovementPoints;
    
    // Calculate new profit at improved margins
    const newProfitMin = validRevenue * (improvedMarginMin / 100);
    const newProfitMax = validRevenue * (improvedMarginMax / 100);
    
    // Additional profit gained per month
    const monthlyGainMin = newProfitMin - currentProfit;
    const monthlyGainMax = newProfitMax - currentProfit;
    
    // Annual projections
    const annualGainMin = monthlyGainMin * 12;
    const annualGainMax = monthlyGainMax * 12;
    
    // PriceStrike Pro pricing
    const proPlanMonthlyCost = 299;
    const proPlanAnnualCost = proPlanMonthlyCost * 12; // $3,588
    
    // Net benefit after Pro subscription
    const netAnnualBenefitMin = annualGainMin - proPlanAnnualCost;
    const netAnnualBenefitMax = annualGainMax - proPlanAnnualCost;
    
    // ROI calculation: (Net Gain / Investment) * 100
    const roiMin = ((netAnnualBenefitMin / proPlanAnnualCost) * 100);
    const roiMax = ((netAnnualBenefitMax / proPlanAnnualCost) * 100);
    
    // Payback period in months
    const avgMonthlyGain = (monthlyGainMin + monthlyGainMax) / 2;
    const paybackMonths = avgMonthlyGain > 0 ? (proPlanAnnualCost / avgMonthlyGain) : 999;
    
    return {
      minImprovementPoints: minImprovementPoints.toFixed(1),
      maxImprovementPoints: maxImprovementPoints.toFixed(1),
      improvedMarginMin: improvedMarginMin.toFixed(1),
      improvedMarginMax: improvedMarginMax.toFixed(1),
      monthlyGainMin: Math.round(monthlyGainMin),
      monthlyGainMax: Math.round(monthlyGainMax),
      annualGainMin: Math.round(annualGainMin),
      annualGainMax: Math.round(annualGainMax),
      proPlanCost: proPlanMonthlyCost,
      proPlanAnnualCost: proPlanAnnualCost,
      netAnnualBenefitMin: Math.round(netAnnualBenefitMin),
      netAnnualBenefitMax: Math.round(netAnnualBenefitMax),
      roiMin: Math.round(Math.max(0, roiMin)),
      roiMax: Math.round(Math.max(0, roiMax)),
      paybackMonths: Math.min(paybackMonths, 999).toFixed(1),
      complexityFactor: complexityFactor.toFixed(2),
      currentProfit: Math.round(currentProfit),
      newProfitMin: Math.round(newProfitMin),
      newProfitMax: Math.round(newProfitMax)
    };
  }, [products, margin, revenue]);

  return (
    <div className="bg-[#0A0F1C] min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-gradient-to-b from-[#0A0F1C] to-[#111827]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-full px-6 py-2 mb-6">
            <Calculator className="w-5 h-5 text-[#2563EB]" />
            <span className="text-[#2563EB] font-medium text-sm">Interactive ROI Calculator</span>
          </div>
          <h1 className="text-6xl md:text-7xl font-bold text-[#F8FAFC] mb-6">
            Calculate Your <span className="text-[#2563EB]">Pricing ROI</span>
          </h1>
          <p className="text-xl text-[#94A3B8] max-w-3xl mx-auto mb-6">
            See how much PriceStrike Pro could increase your profitability. Adjust the sliders below to match your business.
          </p>
          <div className="flex items-center justify-center gap-3">
            <span className="text-[#94A3B8] text-sm">Currency:</span>
            <CurrencySelector />
          </div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-20 px-6 bg-[#0A0F1C]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Input Section */}
            <div className="bg-gradient-to-br from-[#111827] to-[#0A0F1C] border border-[#2563EB]/30 rounded-2xl p-8">
              <h3 className="text-2xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-2">
                <Package className="w-6 h-6 text-[#2563EB]" />
                Your Business Metrics
              </h3>
              
              {/* Products Input */}
              <div className="mb-8">
                <Label className="text-[#F8FAFC] text-sm mb-2 flex items-center justify-between">
                  <span>Number of Products/SKUs</span>
                  <span className="text-[#2563EB] font-semibold text-lg">{products.toLocaleString()}</span>
                </Label>
                <input
                  type="range"
                  min="1"
                  max="10000"
                  step="1"
                  value={products}
                  onChange={(e) => setProducts(Number(e.target.value))}
                  className="w-full h-3 bg-[#2563EB]/20 rounded-lg appearance-none cursor-pointer accent-[#2563EB]"
                />
                <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                  <span>1 SKU</span>
                  <span>10,000+ SKUs</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  💡 More products = More pricing complexity = Higher ROI from automation
                </p>
              </div>

              {/* Margin Input */}
              <div className="mb-8">
                <Label className="text-[#F8FAFC] text-sm mb-2 flex items-center justify-between">
                  <span>Current Average Profit Margin</span>
                  <span className="text-[#2563EB] font-semibold text-lg">{margin}%</span>
                </Label>
                <input
                  type="range"
                  min="0"
                  max="70"
                  step="0.5"
                  value={margin}
                  onChange={(e) => setMargin(Number(e.target.value))}
                  className="w-full h-3 bg-[#2563EB]/20 rounded-lg appearance-none cursor-pointer accent-[#2563EB]"
                />
                <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                  <span>0%</span>
                  <span>70%</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Your current profit margin as a percentage of revenue
                </p>
              </div>

              {/* Revenue Input */}
              <div className="mb-8">
                <Label className="text-[#F8FAFC] text-sm mb-2 flex items-center justify-between">
                  <span>Monthly Revenue</span>
                  <span className="text-[#2563EB] font-semibold text-lg">{formatCompact(revenue)}</span>
                </Label>
                <input
                  type="range"
                  min="1000"
                  max="5000000"
                  step="1000"
                  value={revenue}
                  onChange={(e) => setRevenue(Number(e.target.value))}
                  className="w-full h-3 bg-[#2563EB]/20 rounded-lg appearance-none cursor-pointer accent-[#2563EB]"
                />
                <div className="flex justify-between text-xs text-[#94A3B8] mt-2">
                  <span>{formatCompact(1000)}</span>
                  <span>{formatCompact(5000000)}+</span>
                </div>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Your total monthly revenue across all products
                </p>
              </div>

              {/* Current State Summary */}
              <div className="bg-[#2563EB]/10 border border-[#2563EB]/30 rounded-lg p-4">
                <p className="text-[#F8FAFC] font-semibold mb-2">Your Current State:</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-[#94A3B8]">Monthly Revenue:</p>
                    <p className="text-[#F8FAFC] font-semibold">{format(revenue)}</p>
                  </div>
                  <div>
                    <p className="text-[#94A3B8]">Current Margin:</p>
                    <p className="text-[#F8FAFC] font-semibold">{margin}%</p>
                  </div>
                  <div>
                    <p className="text-[#94A3B8]">Monthly Profit:</p>
                    <p className="text-[#F8FAFC] font-semibold">{format(calculations.currentProfit)}</p>
                  </div>
                  <div>
                    <p className="text-[#94A3B8]">Products:</p>
                    <p className="text-[#F8FAFC] font-semibold">{products.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="bg-gradient-to-br from-[#2563EB]/20 via-[#111827] to-[#0A0F1C] border-2 border-[#2563EB]/50 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-[#2563EB]/10 rounded-full blur-3xl"></div>
              
              <h3 className="text-2xl font-bold text-[#F8FAFC] mb-6 relative z-10 flex items-center gap-2">
                <TrendingUp className="w-6 h-6 text-[#2563EB]" />
                Your Potential Impact
              </h3>
              
              {/* Margin Improvement */}
              <div className="mb-6 relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <Percent className="w-5 h-5 text-[#2563EB]" />
                  <span className="text-[#94A3B8] text-sm">Margin Improvement (Percentage Points)</span>
                </div>
                <p className="text-4xl font-bold text-[#F8FAFC] mb-2">
                  +{calculations.minImprovementPoints}% to +{calculations.maxImprovementPoints}%
                </p>
                <p className="text-[#94A3B8] text-sm">
                  New margin: {calculations.improvedMarginMin}% - {calculations.improvedMarginMax}%
                </p>
                <p className="text-[#2563EB] text-xs mt-2">
                  Complexity factor: {calculations.complexityFactor}x
                </p>
              </div>

              {/* Monthly Gain */}
              <div className="mb-6 relative z-10">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-[#2563EB]" />
                  <span className="text-[#94A3B8] text-sm">Additional Monthly Profit</span>
                </div>
                <p className="text-4xl font-bold text-[#F8FAFC] mb-2">
                  {format(calculations.monthlyGainMin)} - {format(calculations.monthlyGainMax)}
                </p>
                <p className="text-[#94A3B8] text-sm">
                  Annual: {format(calculations.annualGainMin)} - {format(calculations.annualGainMax)}
                </p>
              </div>

              {/* ROI Card */}
              <div className="bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-xl p-6 relative z-10 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="w-5 h-5 text-white" />
                  <span className="text-white/90 text-sm font-semibold">Return on Investment (Year 1)</span>
                </div>
                <p className="text-5xl font-bold text-white mb-3">
                  {calculations.roiMin}% - {calculations.roiMax}%
                </p>
                <div className="bg-white/10 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-white/80">Pro Plan Cost:</p>
                      <p className="text-white font-semibold">{format(calculations.proPlanCost)}/month</p>
                    </div>
                    <div>
                      <p className="text-white/80">Annual Cost:</p>
                      <p className="text-white font-semibold">{format(calculations.proPlanAnnualCost)}</p>
                    </div>
                    <div>
                      <p className="text-white/80">Payback Period:</p>
                      <p className="text-white font-semibold">{calculations.paybackMonths} months</p>
                    </div>
                    <div>
                      <p className="text-white/80">Net Year 1 Gain:</p>
                      <p className="text-white font-semibold">{format(Math.round((calculations.netAnnualBenefitMin + calculations.netAnnualBenefitMax) / 2))}</p>
                    </div>
                  </div>
                </div>
                <p className="text-white/90 text-sm">
                  After deducting Pro subscription: {format(calculations.netAnnualBenefitMin)} - {format(calculations.netAnnualBenefitMax)} net benefit
                </p>
              </div>

              {/* CTA Buttons */}
              <div className="space-y-3 relative z-10">
                <Link to="/pricing">
                  <Button className="w-full bg-white text-[#2563EB] hover:bg-gray-100 px-8 py-6 text-lg font-semibold rounded-lg shadow-xl">
                    View Pricing Plans
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="w-full border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-lg">
                    Talk to Sales
                  </Button>
                </Link>
                <p className="text-[#94A3B8] text-xs text-center">
                  ✓ No credit card required • ✓ 14 day free trial • ✓ Cancel anytime
                </p>
              </div>
            </div>
          </div>

          {/* Methodology & Assumptions */}
          <div className="mt-12 bg-[#111827] border border-[#2563EB]/20 rounded-xl p-8">
            <h3 className="text-xl font-bold text-[#F8FAFC] mb-4">How We Calculate Your ROI</h3>
            <div className="grid md:grid-cols-2 gap-6 text-sm text-[#94A3B8]">
              <div>
                <h4 className="text-[#F8FAFC] font-semibold mb-2">Margin Improvement:</h4>
                <ul className="space-y-1 list-disc list-inside">
                  <li>Baseline: 3-5% margin improvement (percentage points)</li>
                  <li>500-999 SKUs: 1.1x multiplier</li>
                  <li>1,000-1,999 SKUs: 1.15x multiplier</li>
                  <li>2,000-4,999 SKUs: 1.2x multiplier</li>
                  <li>5,000+ SKUs: 1.3x multiplier</li>
                  <li>Based on real customer data from 500+ SMBs</li>
                </ul>
              </div>
              <div>
                <h4 className="text-[#F8FAFC] font-semibold mb-2">ROI Calculation:</h4>
                <ul className="space-y-1 list-disc list-inside">
                  <li>New Profit = Revenue × (Current Margin + Improvement)</li>
                  <li>Monthly Gain = New Profit - Current Profit</li>
                  <li>Annual Gain = Monthly Gain × 12</li>
                  <li>Net Benefit = Annual Gain - Pro Plan Cost ($3,588/year)</li>
                  <li>ROI % = (Net Benefit ÷ Pro Plan Cost) × 100</li>
                  <li>Payback = Pro Annual Cost ÷ Average Monthly Gain</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-[#94A3B8] mt-4 italic">
              * Results are estimates based on aggregate customer data. Actual results may vary based on industry, product mix, and implementation. Conservative estimates used throughout.
            </p>
          </div>

          {/* Social Proof Stats */}
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            <div className="bg-[#111827] border border-[#2563EB]/20 rounded-xl p-6 text-center">
              <p className="text-[#2563EB] text-4xl font-bold mb-2">85%</p>
              <p className="text-[#F8FAFC] font-semibold mb-1">Customer Success Rate</p>
              <p className="text-[#94A3B8] text-sm">Achieve 3%+ margin improvement</p>
            </div>
            <div className="bg-[#111827] border border-[#2563EB]/20 rounded-xl p-6 text-center">
              <p className="text-[#2563EB] text-4xl font-bold mb-2">2.5x</p>
              <p className="text-[#F8FAFC] font-semibold mb-1">Average ROI Multiple</p>
              <p className="text-[#94A3B8] text-sm">Within first 12 months</p>
            </div>
            <div className="bg-[#111827] border border-[#2563EB]/20 rounded-xl p-6 text-center">
              <p className="text-[#2563EB] text-4xl font-bold mb-2">3 weeks</p>
              <p className="text-[#F8FAFC] font-semibold mb-1">Time to First Value</p>
              <p className="text-[#94A3B8] text-sm">From setup to insights</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ROICalculatorPage;
